import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  ViewEncapsulation,
} from '@angular/core';
import { DeviceService } from 'app/shared/service/device.service';
import { ActivatedRoute } from '@angular/router';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { DataSource } from '@angular/cdk/collections';
import { BehaviorSubject, fromEvent, merge, Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { fuseAnimations } from '@fuse/animations';
import { FuseUtils } from '@fuse/utils';
import {ConnectorService} from '../../../auth/connector.service';
import {LocalStorageService} from '../../../service/local-storage.service';

@Component({
  selector: 'app-devices',
  templateUrl: './devices.component.html',
  styleUrls: ['./devices.component.scss'],
  animations: fuseAnimations,
  encapsulation: ViewEncapsulation.None
})
export class DevicesComponent implements OnInit {
  dataSource: FilesDataSource | null;
  length: number;
  pageSize;

  @ViewChild(MatPaginator, { static: true })
  paginator: MatPaginator;

  @ViewChild(MatSort, { static: true })
  sort: MatSort;

  @ViewChild('filter', { static: true })
  filter: ElementRef;

  displayedColumns;
  authorization;

  // Private
  private _unsubscribeAll: Subject<any>;

  constructor(
    private deviceService: DeviceService,
    private route: ActivatedRoute,
    private connectorService: ConnectorService,
    private localStorageService: LocalStorageService
  ) {
    // Set the private defaults
    this._unsubscribeAll = new Subject();
    this.authorization = this.connectorService.getAuthorization('devices');
  }

  /**
   * On init
   */
  ngOnInit(): void {
    if (this.authorization.length < 1) {
      this.authorization = null;
      this.displayedColumns = ['macAddress', 'sensorType', 'deviceModel', 'param1', 'dateStart', 'dateEnd'];
    } else {
      this.displayedColumns = ['macAddress', 'sensorType', 'deviceModel', 'param1', 'dateStart', 'dateEnd', 'Action'];
    }
    this.dataSource = new FilesDataSource(
      this.deviceService,
      this.paginator,
      this.sort
    );

    this.getPageSize();

    if (this.filter != null) {
      fromEvent(this.filter.nativeElement, 'keyup')
        .pipe(
          takeUntil(this._unsubscribeAll),
          debounceTime(150),
          distinctUntilChanged()
        )
        .subscribe(() => {
          if (!this.dataSource) {
            return;
          }

          this.filter = this.filter.nativeElement.value;
        });
    }
  }

  getPageSize(): void {
    this.pageSize = this.localStorageService.get('pageSize');
  }

  setPageSize(pageEvent): void {
    const newPageSize = pageEvent.pageSize;
    this.localStorageService.set('pageSize', newPageSize );
  }/**/
}

export class FilesDataSource extends DataSource<any> {
  private _filterChange = new BehaviorSubject('');
  private _filteredDataChange = new BehaviorSubject('');

  /**
   * Constructor
   *
   * @param {DeviceService} deviceService
   * @param {MatPaginator} _matPaginator
   * @param {MatSort} _matSort
   */
  constructor(
    private deviceService: DeviceService,
    private _matPaginator: MatPaginator,
    private _matSort: MatSort
  ) {
    super();

    this.filteredData = this.deviceService.devices;
  }
  /**
   * Connect function called by the table to retrieve one stream containing the data to render.
   *
   * @returns {Observable<any[]>}
   */
  connect(): Observable<any[]> {
    const displayDataChanges = [
      this.deviceService.onDevicesChanged,
      this._matPaginator.page,
      this._filterChange,
      this._matSort.sortChange
    ];

    return merge(...displayDataChanges).pipe(
      map(() => {
        let data = this.deviceService.devices.slice();

        data = this.filterData(data);

        this.filteredData = [...data];

        data = this.sortData(data);

        // Grab the page's slice of data.
        const startIndex =
          this._matPaginator.pageIndex * this._matPaginator.pageSize;
        return data.splice(startIndex, this._matPaginator.pageSize);
      })
    );
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  // Filtered data
  get filteredData(): any {
    return this._filteredDataChange.value;
  }

  set filteredData(value: any) {
    this._filteredDataChange.next(value);
  }

  // Filter
  get filter(): string {
    return this._filterChange.value;
  }

  set filter(filter: string) {
    this._filterChange.next(filter);
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Filter data
   *
   * @param data
   * @returns {any}
   */
  filterData(data): any {
    if (!this.filter) {
      return data;
    }
    return FuseUtils.filterArrayByString(data, this.filter);
  }

  /**
   * Length data
   *
   * @param data
   * @returns {any[]}
   */
  lengthData(data): any {
    return data.length;
  }

  /**
   * Sort data
   *
   * @param data
   * @returns {any[]}
   */
  sortData(data): any[] {
    if (!this._matSort.active || this._matSort.direction === '') {
      return data;
    }

    return data.sort((a, b) => {
      let propertyA: number | string = '';
      let propertyB: number | string = '';

      switch (this._matSort.active) {
        case 'macAddress':
          [propertyA, propertyB] = [a.macAddress, b.macAddress];
          break;
        case 'sensorType':
          [propertyA, propertyB] = [a.SensorType.name, b.SensorType.name];
          break;
        case 'deviceModel':
          [propertyA, propertyB] = [a.DeviceModel, b.DeviceModel];
          break;
        case 'param1':
          [propertyA, propertyB] = [a.param1, b.param1];
          break;
        case 'dateStart':
          [propertyA, propertyB] = [a.dateStart, b.dateStart];
          break;
        case 'dateEnd':
          [propertyA, propertyB] = [a.dateEnd, b.dateEnd];
          break;
      }

      const valueA = isNaN(+propertyA) ? propertyA : +propertyA;
      const valueB = isNaN(+propertyB) ? propertyB : +propertyB;

      return (
        (valueA < valueB ? -1 : 1) *
        (this._matSort.direction === 'asc' ? 1 : -1)
      );
    });
  }

  /**
   * Disconnect
   */
  disconnect(): void {}
}
